import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { AuthService } from './auth.service';
import { permissionSlugs } from './utils/permission.utils';
import { AuthzAccessHintService } from './shared/authz-access-hint/authz-access-hint.service';
import { AlertDialogService } from './shared/alert-dialog/alert-dialog.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuardService implements CanActivate {
  constructor(
    public auth: AuthService,
    public router: Router,
    private authzAccessHint: AuthzAccessHintService,
    private alertDialog: AlertDialogService,
  ) { }

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    if (!(await this.auth.isAuthenticated())) {
      this.router.navigate(['/login']);
      return false;
    }

    // Route ACL: permissionActions are resolved to legacy or Guardian slugs for data.feature.
    // allowWhenPanelOff keeps a screen open until that feature is listed in AUTHZ_PANELS.
    const feature = route.data && route.data.feature;
    if (route.data && route.data.allowWhenPanelOff && (!feature || !this.auth.getAuthzPanels().includes(feature))) {
      return true;
    }
    const permissionActions = route.data && route.data.permissionActions;
    const requiredPermission = permissionActions
      ? permissionSlugs(feature, permissionActions, this.auth.getAuthzPanels())
      : route.data && route.data.permission;

    try {
      const allowed = this.auth.isAuthorizedFor(requiredPermission, feature);
      if (!allowed) {
        await this.authzAccessHint.showAccessDeniedDialogForFeaturePanel(feature);
        return this.resolveDeniedNavigation();
      }
    } catch (error) {
      await this.alertDialog.open({
        title: 'Authorization Error',
        message: 'Failed to fetch user permissions from authorization service. Please try again or contact support.',
        confirmText: 'OK',
      });
      return this.resolveDeniedNavigation();
    }

    return true;
  }

  // Direct URL / refresh: land on home so the sidenav shell is shown. In-app navigation: stay on the current screen.
  private resolveDeniedNavigation(): boolean | UrlTree {
    if (this.shouldRedirectToHomeAfterDeny()) {
      return this.router.createUrlTree(['/']);
    }
    return false;
  }

  private shouldRedirectToHomeAfterDeny(): boolean {
    const nav = this.router.getCurrentNavigation();
    return !nav || nav.previousNavigation == null;
  }
}
